import { useCallback, useMemo, useState } from 'react'
import { closestCenter, DndContext } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'
import { v4 as uuid4 } from 'uuid'

import { IActionRepeaterValue, IActionSettings, IActionType } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { ActionVerticalNodeAtom, AddActionVerticalNodeModal, Button, ButtonVariant } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useActionFormContextV2, type ActionTriggerSource } from '@/context/ActionFormContextV2'
import { isActionHasSettings, isActionPlugin } from '@/context/ActionFormProviderV2'
import { usePluginActions } from '@/hooks/usePluginActions'
import { useSortableHandle } from '@/hooks/useSortableHandle'
import { capitalize } from '@/shared/utils'

import { AdvancedWorkflowEditor, WorkflowFullscreenModal } from './action-workflows'
import { ActionFormModal } from './ActionFormModal'
import { useGenerateActionDescription } from './utils'

const updateActionTypes = [
  IActionType.DeleteRow,
  IActionType.DuplicateRow,
  IActionType.UpdateRow,
  IActionType.ToggleValue,
  IActionType.IncrementValue,
]

interface ActionRepeaterProps {
  id: string
  values: IActionRepeaterValue[]
  onChange?: (nextValues: IActionRepeaterValue[]) => void
  noWrap?: boolean
  excludeActions?: IActionType[]
  triggerSource?: ActionTriggerSource
  label?: string
}

/**
 * Action Repeater
 *
 * @todo add show context menu action form
 */
export function ActionRepeaterV2({
  id,
  values: actionValues = [],
  onChange,
  noWrap = false,
  excludeActions = [],
  triggerSource,
  label = 'Action Sequence:',
}: ActionRepeaterProps) {
  const { activeComponent, activeLayerElementSettings, isDetailPage } = useActiveLayerContext()
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isAdvancedEditorOpen, setIsAdvancedEditorOpen] = useState(false)
  // get the tableId (datasource) from 'list' component
  const isList = activeComponent?.groupId === 'list'
  const elSettings = !activeComponent ? undefined : activeLayerElementSettings[activeComponent.id]
  const elSettingsDataSourceKey = !elSettings
    ? undefined
    : Object.keys(elSettings).find((key) => key.includes('data-source'))
  const elSettingsTableId =
    elSettings && elSettingsDataSourceKey ? (elSettings[elSettingsDataSourceKey] as string) : undefined

  // Fetch plugin actions
  const { pluginActions, loading: loadingPlugins } = usePluginActions()

  // Map plugin actions to custom actions format
  const customPluginActions = useMemo(() => {
    if (!pluginActions) return []

    return pluginActions.flatMap((plugin) =>
      (plugin.actions || []).map((action) => ({
        type: `${plugin.name} - ${action._Label.en}`,
        label: `${plugin.name} - ${action._Label.en}`,
        description: action._Description.en,
        icon: (
          <img
            src={action.icon}
            alt={`${plugin.name} - ${action._Label.en}`}
            style={{ width: 20, height: 20, objectFit: 'contain' }}
          />
        ),
        // Store the action configuration for form generation
        settings: {
          pluginId: plugin.id,
          fields: action._Request._Fields,
          actionKey: action._Key,
          icon: action.icon,
          pluginDetail: plugin.pluginDetail,
        },
      }))
    )
  }, [pluginActions])

  // Dnd State
  const { sensors, handleDragEnd } = useSortableHandle((active, over) => {
    const oldIndex = actionValues.findIndex((p) => p.id === active.id)
    const newIndex = actionValues.findIndex((p) => p.id === over.id)
    onChange?.(arrayMove(actionValues, oldIndex, newIndex))
  })

  // ===== Action form handler =======
  const { openActionForm } = useActionFormContextV2()

  // Local State
  const [activeDropdown, setActiveDropdown] = useState('')

  // handle update action values
  const handleOnSave = useCallback(
    (id: string, action: IActionType, data?: IActionSettings) => {
      const idx = actionValues.findIndex((v) => v.id === id)
      let actionSettingsArr = [...actionValues]

      if (idx !== -1) {
        // update action
        actionSettingsArr[idx].type = action
        if (!!data) actionSettingsArr[idx].settings = data
      } else {
        // add new action
        actionSettingsArr.push({
          id,
          type: action,
          settings: data || {},
        })
      }

      onChange?.(actionSettingsArr)
      if (activeDropdown) setActiveDropdown('')
    },
    [onChange, actionValues, activeDropdown]
  )

  // handle change action type
  const handleChangeAction = useCallback(
    async (id: string, action: IActionType | string) => {
      const value = actionValues.find((v) => v.id === id)

      if (value && isActionPlugin(value)) {
        const pluginAction = value.settings.pluginAction
        // Handle plugin action
        openActionForm(
          {
            id,
            type: action as IActionType,
            settings: {
              ...value?.settings,
              pluginAction,
            },
          },
          triggerSource
        )
      } else if (!!value && isActionHasSettings(action as IActionType)) {
        // Handle regular action with settings
        openActionForm(
          {
            id,
            type: action as IActionType,
            settings: value.type === action ? value.settings : {},
          },
          triggerSource
        )
      } else {
        let settings: IActionSettings = {}

        // add element datasource as `tableId` for the list component action
        // the included actions are: IActionType.DeleteRow, IActionType.DuplicateRow
        if (!isDetailPage && !!isList) {
          let isUpdateAction = updateActionTypes.some((a) => a === action)
          if (isUpdateAction) {
            settings.tableId = elSettingsTableId
          }
        }

        handleOnSave(id, action as IActionType, settings)
      }
    },
    [actionValues, openActionForm, triggerSource, isDetailPage, isList, handleOnSave, elSettingsTableId]
  )

  // handle delete action
  const handleDeleteAction = useCallback(
    (id: string) => {
      onChange?.(actionValues.filter((v) => v?.id !== id))
    },
    [onChange, actionValues]
  )

  const actionContainAdvanceStructure = actionValues.find((value) => Object.hasOwn(value, 'nextAction'))

  const advanceActionName =
    actionValues[0]?.settings?.advanceActionName ?? `${capitalize(triggerSource)} ${label} Workflow`

  if (loadingPlugins) {
    return (
      <div className='flex flex-col gap-4 w-full'>
        <div className='animate-pulse h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4' />
        <div className='animate-pulse h-12 w-full bg-gray-200 dark:bg-gray-700 rounded mb-2' />
        <div className='animate-pulse h-12 w-full bg-gray-200 dark:bg-gray-700 rounded mb-2' />
        <div className='animate-pulse h-12 w-full bg-gray-200 dark:bg-gray-700 rounded mb-2' />
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'flex flex-col gap-0 w-full',
        !noWrap && 'p-4 border bdr rounded-md bg-zinc-50 dark:bg-slate-700'
      )}
    >
      {<div className='text-zinc-600 dark:text-slate-300 mb-4'>Action Sequence</div>}

      {actionValues.length === 0 && (
        <div className='text-center py-4 text-gray-500 dark:text-slate-400 text-xs font-medium'>
          Add an action to get started
        </div>
      )}

      {actionValues.length > 0 && (
        <div className='flex flex-col w-full relative gap-4'>
          {!actionContainAdvanceStructure && (
            <div className='flex justify-center absolute left-1/2 -translate-x-1/2 top-0 h-full'>
              <div className='w-0.5 h-full bg-gray-200 dark:bg-gray-600'></div>
            </div>
          )}

          {actionContainAdvanceStructure && (
            <ActionVerticalNodeAtom
              onEdit={() => {
                setIsAdvancedEditorOpen(true)
              }}
              onDelete={() => {
                onChange?.([])
              }}
              actionName={advanceActionName}
              actionId={IActionType.ShowDialog}
              actionDescription=''
              listener={{}}
              hideSort
              hideEdit
              icon={<MaterialIcon icon='bolt' size={20} className='text-orange-500 dark:text-orange-400' fill />}
            />
          )}

          {!actionContainAdvanceStructure && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToFirstScrollableAncestor]}
            >
              <SortableContext items={actionValues}>
                {actionValues?.map((value) => {
                  let currVal = value
                  if (!!currVal && !currVal.id) currVal.id = uuid4()

                  if (currVal) {
                    return (
                      <SortableActionItem
                        key={currVal.id}
                        value={currVal}
                        onChangeAction={handleChangeAction}
                        onUpdateAction={(act) => openActionForm(act, triggerSource)}
                        onDeleteAction={handleDeleteAction}
                        activeDropdown={activeDropdown}
                        setActiveDropdown={setActiveDropdown}
                        excludeActions={excludeActions}
                        hasSettings={(currVal.type && isActionHasSettings?.(currVal.type)) || isActionPlugin(currVal)}
                        customPluginActions={customPluginActions}
                      />
                    )
                  }

                  return null
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
      <div>
        {actionValues.length > 0 && !actionContainAdvanceStructure && (
          <div className='flex justify-center'>
            <div className='w-0.5 h-4 bg-gray-200 dark:bg-gray-600'></div>
          </div>
        )}

        {!actionContainAdvanceStructure && (
          <Button
            variant={ButtonVariant.Ghost}
            className='h-8 w-8 p-0 rounded-lg border-2 border-gray-200 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-300 cursor-pointer mx-auto'
            onClick={() => setIsAddOpen(true)}
          >
            <MaterialIcon icon='add' size={16} className='text-gray-500 dark:text-slate-400' />
          </Button>
        )}
      </div>

      <div
        className='text-center py-4 text-gray-500 dark:text-slate-400 underline text-sm font-medium cursor-pointer'
        onClick={() => setIsAdvancedEditorOpen(true)}
      >
        Edit in a Workflow
      </div>

      <WorkflowFullscreenModal
        isOpen={isAdvancedEditorOpen}
        onClose={() => setIsAdvancedEditorOpen(false)}
        title={advanceActionName}
        onTitleRename={(newName) => {
          // Update the first action's settings to include the new name
          if (actionValues.length > 0) {
            const updatedValues = [...actionValues]
            updatedValues[0] = {
              ...updatedValues[0],
              settings: {
                ...updatedValues[0].settings,
                advanceActionName: newName,
              },
            }
            onChange?.(updatedValues)
          }
        }}
      >
        <AdvancedWorkflowEditor
          excludeActions={[...excludeActions, IActionType.StartSMS]}
          triggerLabel={label}
          nodes={actionValues}
          onSave={(connectedNodes) => {
            // Convert WorkflowNode[] back to IActionRepeaterValue[]
            const normalizedActions: IActionRepeaterValue[] = connectedNodes.map((node) => ({
              id: node.id,
              type: node.type,
              settings: node.settings || {},
              nextAction: node.nextAction || null,
              label: node.label || '',
            }))

            // Trigger onChange with the normalized data
            onChange?.(normalizedActions)
          }}
          onClose={() => setIsAdvancedEditorOpen(false)}
          customPluginActions={customPluginActions}
        />
      </WorkflowFullscreenModal>

      <AddActionVerticalNodeModal
        show={isAddOpen}
        excludeActions={[
          IActionType.PerformNextAction,
          IActionType.ConditionalIf,
          IActionType.ConditionalSwitch,
          IActionType.FilterData,
          IActionType.TemporaryVariable,
          IActionType.StartSMS,
          ...excludeActions,
        ]}
        onHide={() => setIsAddOpen(false)}
        onAddNode={(value) => {
          let newId = uuid4()

          const pluginAction = customPluginActions.find((pa) => pa.type === value.action)

          if (pluginAction) {
            openActionForm(
              {
                id: newId,
                type: value.action,
                settings:
                  value.action === IActionType.None
                    ? {}
                    : {
                        ...(value.settings || {}),
                        pluginAction: pluginAction.settings,
                      },
              },
              triggerSource
            )
            return
          }

          if (!!value && isActionHasSettings(value.action)) {
            openActionForm(
              {
                id: newId,
                type: value.action,
                settings: value.action === IActionType.None ? {} : value.settings || {},
              },
              triggerSource
            )
            return
          }

          onChange?.([
            ...(actionValues || {}),
            {
              id: newId,
              type: value.action,
              settings: {},
            },
          ])
        }}
        nodesCount={actionValues.length}
        customActions={customPluginActions}
      />

      <ActionFormModal onSubmit={handleOnSave} />
    </div>
  )
}

interface SortableActionItemProps {
  value: IActionRepeaterValue
  onChangeAction: (id: string, value: IActionType) => void
  onUpdateAction: (value: IActionRepeaterValue) => void
  onDeleteAction: (id: string) => void
  activeDropdown?: string
  setActiveDropdown: (item: string) => void
  excludeActions: IActionType[]
  hasSettings: boolean
  customPluginActions: any[]
}

function SortableActionItem({
  value,
  onUpdateAction,
  onDeleteAction,
  hasSettings,
  customPluginActions,
}: SortableActionItemProps) {
  const { attributes, listeners, setNodeRef, transition, transform } = useSortable({
    id: value.id,
  })

  const icon = useMemo(() => {
    const pluginAction = customPluginActions.find((pa) => pa.type === value.type)
    return pluginAction?.icon
  }, [customPluginActions, value.type])

  const { generateActionDescription } = useGenerateActionDescription()

  const actionDescription = useMemo(() => {
    return generateActionDescription(value, customPluginActions)
  }, [value, customPluginActions, generateActionDescription])

  return (
    <div
      className='w-full flex-col gap-4 relative z-10'
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
    >
      <ActionVerticalNodeAtom
        key={value.id}
        onEdit={() => {
          onUpdateAction(value)
        }}
        onDelete={() => {
          onDeleteAction(value.id)
        }}
        actionName={value.type.toString()}
        actionDescription={actionDescription || ''}
        actionId={value.id}
        listener={listeners}
        hideEdit={!hasSettings}
        icon={icon}
      />
    </div>
  )
}
